import { useMemo, useState } from "react";
import { TIER_COLORS } from "../utils/tiers";
import { getGroupSchedule, formatRange } from "../utils/time";
import { rankPlayersForGroup } from "../utils/playerFit";

function withAge(p) {
  return p.age != null ? `${p.name} (${p.age})` : p.name;
}

function groupLabel(g) {
  const { startTime, duration } = getGroupSchedule(g);
  const when = g.day ? `${g.day}${startTime ? ` ${formatRange(startTime, duration)}` : ""}` : "no day/time";
  return `${g.id} · ${g.tier} · ${when} · ${g.playerIds.length}/4`;
}

// Inverse of "Find a Time": pick a group, then rank the whole roster by how
// well each player fits it (availability, tier, preferred partners, age),
// with the reasons shown and one-click add.
export default function FindPlayerView({ players, groups, onPlayerClick, onJoinGroup, onNotify }) {
  const [groupId, setGroupId] = useState("");
  const [search, setSearch] = useState("");
  const [onlyFree, setOnlyFree] = useState(false);

  const group = groups.find(g => g.id === groupId) || null;

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })),
    [groups]
  );

  const ranked = useMemo(
    () => (group ? rankPlayersForGroup(group, players) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId, groups, players]
  );

  const visible = ranked.filter(r => {
    if (onlyFree && r.available !== true) return false;
    if (search.trim() && !r.player.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  const colors = group ? (TIER_COLORS[group.tier] || TIER_COLORS["No Group"]) : null;

  function handleAdd(player) {
    onJoinGroup(group.id, [player.id]);
    onNotify?.({ message: `Added ${player.name} to ${group.id}` });
  }

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div>
        <h2 className="text-base font-display text-xl text-brand-blue mb-1">Find Player</h2>
        <p className="text-sm text-gray-500">
          Pick a group to see every player ranked by how well they fit — availability, tier, preferred partners and age.
        </p>
      </div>

      {/* Group picker */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Group</label>
        <select
          value={groupId}
          onChange={e => setGroupId(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">Select a group…</option>
          {sortedGroups.map(g => (
            <option key={g.id} value={g.id}>{groupLabel(g)}</option>
          ))}
        </select>
      </div>

      {!group ? (
        <p className="text-sm text-gray-400 italic">Select a group to rank players by fit.</p>
      ) : (
        <>
          {/* Selected group summary */}
          <div className={`${colors.bg} ${colors.border} border rounded-lg p-3`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className={`font-mono text-sm font-bold ${colors.text}`}>{group.id} · {group.tier}</div>
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${group.playerIds.length >= 4 ? "bg-red-200 text-red-800" : "bg-white text-gray-600 border border-gray-200"}`}>
                {group.playerIds.length}/4 {group.playerIds.length >= 4 ? "· full" : "· open"}
              </span>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {group.day ? `${group.day} ${(() => { const s = getGroupSchedule(group); return s.startTime ? formatRange(s.startTime, s.duration) : ""; })()}` : "No day/time set"}
              {group.court ? ` · ${group.court}` : ""}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Filter players by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={onlyFree} onChange={e => setOnlyFree(e.target.checked)} />
              Only players free at this time
            </label>
            <span className="text-xs text-gray-400 sm:ml-auto">{visible.length} shown</span>
          </div>

          {/* Ranked results */}
          <div className="space-y-2">
            {visible.map(r => (
              <FitRow
                key={r.player.id}
                result={r}
                groupFull={group.playerIds.length >= 4}
                onAdd={handleAdd}
                onPlayerClick={onPlayerClick}
              />
            ))}
            {visible.length === 0 && (
              <p className="text-sm text-gray-400 italic">No players match the current filter.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FitRow({ result, groupFull, onAdd, onPlayerClick }) {
  const { player, score, available, inGroup, reasons } = result;
  const c = TIER_COLORS[player.tier] || TIER_COLORS["No Group"];

  const availBadge = available === true
    ? { text: "Free", cls: "bg-green-200 text-green-800" }
    : available === false
      ? { text: "Not free", cls: "bg-amber-200 text-amber-800" }
      : { text: "—", cls: "bg-gray-200 text-gray-600" };

  return (
    <div className={`border rounded-lg p-3 ${inGroup ? "border-gray-200 bg-gray-50 opacity-75" : "border-gray-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => onPlayerClick(player)} className="font-medium text-sm text-gray-800 hover:underline truncate">
            {withAge(player)}
          </button>
          <span className={`${c.bg} ${c.border} border text-xs px-1.5 py-0.5 rounded ${c.text} whitespace-nowrap`}>{player.tier}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${availBadge.cls}`}>{availBadge.text}</span>
          <span className="text-xs px-2 py-0.5 rounded font-semibold bg-blue-100 text-blue-800" title="Fit score">fit {score}</span>
        </div>
      </div>

      {/* Fit reasons */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
        {reasons.map((rn, i) => (
          <span
            key={i}
            className={`text-xs ${rn.kind === "pos" ? "text-green-700" : rn.kind === "neg" ? "text-red-600" : "text-gray-400"}`}
          >
            {rn.kind === "pos" ? "✓" : rn.kind === "neg" ? "⚠" : "·"} {rn.text}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {!inGroup && (
          <button
            onClick={() => onAdd(player)}
            disabled={groupFull}
            className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded px-2.5 py-1 font-medium transition-colors"
            title={groupFull ? "Group is full (4/4)" : `Add ${player.name} to the group`}
          >
            + Add to group
          </button>
        )}
        <button
          onClick={() => onPlayerClick(player)}
          className="text-xs border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 rounded px-2.5 py-1 font-medium transition-colors"
        >
          Open card
        </button>
      </div>
    </div>
  );
}
